import { useEffect, useState } from 'react';
import { Box, Button, Chip, Divider, TextField, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider, Resolver, useFieldArray } from 'react-hook-form';
import * as yup from 'yup';
import { useQueryClient } from 'react-query';
import { yupResolver } from '@hookform/resolvers/yup';

import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { CreateWorkflowDto, WorkflowResponseDto } from '@bandido/shared/dtos';

import { useCreateWorkflowMutation, useUpdateWorkflowMetadataMutation } from '../../queries';
import { QueryKeys } from '@frontend/features/common/types';
import { router } from '@frontend/router/router';
import { routes } from '@frontend/router/routes';
import { LoadingButton } from '@mui/lab';

interface FormProps {
  onClose: () => void;
  agencyId: string;
  isEditMode?: boolean;
  data?: Partial<WorkflowResponseDto>;
}

interface EmailDomain {
  value: string;
}

interface CreateWorkflow extends Omit<CreateWorkflowDto, 'emailDomains'> {
  emailDomains: EmailDomain[];
}

export const Form = ({ onClose, agencyId, isEditMode, data }: FormProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const [emailDomainValue, setEmailDomainValue] = useState('');

  const action = isEditMode ? 'edit' : 'create';

  const { mutate: createWorkflow, isLoading: isCreating } = useCreateWorkflowMutation({
    onSuccess: ({ data }) => {
      addToast({
        message: t(`workflows.${action}.success`),
        color: 'success',
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getAllWorkflows] });
      const newWorkflowRoute = `${routes.dashboard.agencyView.root.replace(
        ':id',
        agencyId,
      )}/${routes.dashboard.agencyView.workflow.replace(':workflowId', String(data.id))}`;

      onClose();
      router.navigate(newWorkflowRoute);
    },
    onError: () => {
      addToast({
        message: t(`workflows.${action}.error`),
        color: 'error',
      });
    },
  });
  const { mutate: updateWorkflow, isLoading: isUpdating } = useUpdateWorkflowMetadataMutation(String(data?.id), {
    onSuccess: () => {
      addToast({
        message: t(`workflows.${action}.success`),
        color: 'success',
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getAllWorkflows] });
      onClose();
    },
    onError: () => {
      addToast({
        message: t(`workflows.${action}.error`),
        color: 'error',
      });
    },
  });

  const resolver = yupResolver(
    yup.object().shape({
      name: yup.string().required(),
      color: yup.string().required(),
    }),
  ) as unknown as Resolver<CreateWorkflow>;

  const methods = useForm<CreateWorkflow>({
    defaultValues: { color: '', name: '', emailDomains: [] },
    resolver,
  });
  const { handleSubmit, control } = methods;
  const { fields, append, remove } = useFieldArray<CreateWorkflow>({
    name: 'emailDomains',
    control,
  });

  useEffect(() => {
    if (isEditMode && data) {
      const emailDomains = data.emailDomains?.map((em) => ({ value: em }));

      return methods.reset({ ...data, emailDomains });
    }
    methods.reset({
      name: '',
      color: '',
      emailDomains: [],
    });
  }, [data]);

  const onSubmit = async (data: CreateWorkflow) => {
    const emailDomains = data.emailDomains.map((em) => em.value);
    const actionPayload = {
      name: data.name,
      color: data.color,
      agencyId: Number(agencyId),
      emailDomains,
    };

    if (isEditMode) {
      updateWorkflow(actionPayload);
    } else {
      createWorkflow(actionPayload);
    }
  };

  const validateEmailDomains = (emailDomainValue: string) => {
    const emailRegex = /^(\*+|[a-zA-Z0-9._%+-]+)?@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (emailRegex.test(emailDomainValue)) {
      append({ value: emailDomainValue });

      setEmailDomainValue('');
    } else {
      addToast({
        message: t('workflows.form.error.invalidEmailDomain', { emailDomainValue }),
        color: 'error',
      });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' && emailDomainValue !== '') {
      event.preventDefault();
      validateEmailDomains(emailDomainValue);
    }
  };

  const handleBlur = (): void => {
    if (emailDomainValue.trim() !== '') {
      validateEmailDomains(emailDomainValue);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gap={2}
          sx={{
            maxHeight: 'calc(100vh - 300px)',
            paddingTop: theme.spacing(1),
          }}
        >
          <Box gridColumn="span 12">
            <ControlledTextField
              control={methods.control}
              name="name"
              label={t('workflows.form.name')}
              placeholder={t('common.form.onlyLetters')}
              required
              errorMessage={t('common.form.required')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 10">
            <ControlledTextField
              control={methods.control}
              name="color"
              label={t('workflows.form.color')}
              placeholder={t('common.form.onlyLetters')}
              required
              errorMessage={t('common.form.required')}
              fullWidth
            />
          </Box>

          <Box gridColumn="span 2">
            <ControlledTextField
              control={methods.control}
              name="color"
              type="color"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>

          <Box gridColumn="span 12">
            <TextField
              sx={{
                '& .MuiInputBase-root': {
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                },
                '& .MuiInputBase-input': {
                  flex: 1,
                },
                p: {
                  background: 'transparent',
                },
              }}
              variant="outlined"
              value={emailDomainValue}
              onChange={(event) => setEmailDomainValue(event.target.value)}
              label={t('workflows.form.domains')}
              helperText={t('workflows.form.domainsHint')}
              size="small"
              fullWidth
              placeholder="john@doe.com"
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      padding: (theme) => theme.spacing(1, 0),
                    }}
                  >
                    {fields.map((field, index) => (
                      <Chip key={field.id} label={field.value} onDelete={() => remove(index)} />
                    ))}
                  </Box>
                ),
              }}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" gap={theme.spacing(3)} paddingTop={theme.spacing(3)}>
          <Divider />
          <Box
            display="flex"
            justifyContent="space-evenly"
            width="100%"
            gap={theme.spacing(1)}
            sx={{
              '& > button': {
                width: 'calc(50% - 4px)',
              },
            }}
          >
            <Button variant="outlined" color="error" size="medium" onClick={onClose}>
              {t('common.form.discard')}
            </Button>
            <LoadingButton
              variant="contained"
              loading={isCreating || isUpdating}
              color="primary"
              type="submit"
              size="medium"
            >
              {t('common.form.save')}
            </LoadingButton>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};
